import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api/axios';
import { HeartFillIcon, LockIcon } from '../../utils/icons';

const PROMPT_TEXTS = {
  simple_pleasures: 'My simple pleasures...',
  geek_out: 'I geek out on...',
  life_goal: 'A life goal of mine...',
  unusual_skill: 'My most unusual skill...',
  green_flags: 'My green flags...',
  perfect_sunday: 'My perfect Sunday...',
  deal_breaker: 'My biggest deal breaker...',
  best_travel: 'Best travel story...',
  dinner_guest: 'Dream dinner guest...',
  hot_take: 'My most controversial opinion...',
  love_language: 'My love language is...',
  pet_peeve: 'My biggest pet peeve...',
  guilty_pleasure: 'My guilty pleasure...',
  two_truths_lie: 'Two truths and a lie...',
  typical_sunday: 'You can find me on a typical Sunday...',
  first_thing_noticed: 'First thing people notice about me...',
  never_do_again: 'Something I will never do again...',
  most_spontaneous: 'Most spontaneous thing I have done...',
  believe_in: 'I believe that...',
  change_mind: 'Something that changed my mind...',
};

const TRAIT_LABELS = {
  smoking: 'Smoking', drinking: 'Drinking', cannabis: 'Cannabis', diet: 'Diet',
  exercise: 'Exercise', sleep_schedule: 'Sleep', education: 'Education',
  religion: 'Religion', politics: 'Politics', languages: 'Languages',
  ethnicity: 'Ethnicity', height: 'Height', body_type: 'Body type',
  has_kids: 'Has kids', wants_kids: 'Wants kids', pets: 'Pets',
  communication_style: 'Communication', social_energy: 'Social energy',
  relationship_goal: 'Looking for', love_language_trait: 'Love language',
};

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.5)',
    zIndex: 200,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    backdropFilter: 'blur(4px)',
  },
  modal: {
    background: 'var(--bg-surface)',
    borderRadius: 'var(--radius-lg)',
    width: '100%',
    maxWidth: '440px',
    maxHeight: '85vh',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    border: '1px solid var(--border-color)',
    boxShadow: 'var(--shadow-lg)',
  },
  photoSection: {
    position: 'relative',
    width: '100%',
    aspectRatio: '4/3',
    background: 'var(--bg-elevated)',
    flexShrink: 0,
  },
  photo: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  noPhoto: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    fontSize: '48px',
    color: 'var(--text-muted)',
  },
  photoNav: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    display: 'flex',
    gap: '3px',
    padding: '8px 8px 0',
    zIndex: 2,
  },
  photoDot: {
    flex: 1,
    height: '3px',
    borderRadius: '1.5px',
    background: 'rgba(255,255,255,0.3)',
  },
  photoDotActive: {
    background: '#fff',
  },
  closeBtn: {
    position: 'absolute',
    top: '10px',
    right: '10px',
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    background: 'rgba(0,0,0,0.5)',
    border: 'none',
    color: '#fff',
    fontSize: '18px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 3,
  },
  gradient: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '80px',
    background: 'linear-gradient(transparent, rgba(0, 0, 0, 0.6))',
  },
  nameOverlay: {
    position: 'absolute',
    bottom: '12px',
    left: '16px',
    right: '16px',
    zIndex: 2,
  },
  name: {
    fontSize: '22px',
    fontWeight: '700',
    color: '#fff',
  },
  metaRow: {
    display: 'flex',
    gap: '6px',
    marginTop: '6px',
    flexWrap: 'wrap',
  },
  content: {
    flex: 1,
    overflowY: 'auto',
    padding: '16px',
  },
  bio: {
    fontSize: '14px',
    color: 'var(--text-secondary)',
    lineHeight: 1.6,
    marginBottom: '16px',
  },
  section: {
    marginBottom: '16px',
  },
  sectionTitle: {
    fontSize: '12px',
    fontWeight: '600',
    color: 'var(--text-muted)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '8px',
  },
  promptCard: {
    background: 'var(--bg-elevated)',
    borderRadius: 'var(--radius-sm)',
    padding: '12px',
    marginBottom: '8px',
  },
  promptQ: {
    fontSize: '12px',
    color: 'var(--text-muted)',
    marginBottom: '4px',
  },
  promptA: {
    fontSize: '14px',
    lineHeight: 1.4,
  },
  traitChips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '4px',
  },
  traitChip: {
    fontSize: '11px',
    padding: '3px 8px',
    borderRadius: '10px',
    background: 'var(--bg-elevated)',
    color: 'var(--text-secondary)',
    border: '1px solid var(--border-color)',
  },
  vaxGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '4px',
  },
  actions: {
    display: 'flex',
    gap: '10px',
    padding: '12px 16px',
    borderTop: '1px solid var(--border-color)',
    flexShrink: 0,
  },
  actionBtn: {
    flex: 1,
    padding: '10px',
    borderRadius: 'var(--radius-sm)',
    border: 'none',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 200ms',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
  },
  likeBtn: {
    background: 'var(--accent)',
    color: '#fff',
  },
  chatBtn: {
    background: 'var(--accent)',
    color: '#fff',
  },
  passBtn: {
    background: 'var(--bg-elevated)',
    color: 'var(--text-secondary)',
    border: '1px solid var(--border-color)',
  },
  statusBanner: {
    padding: '8px 16px',
    fontSize: '13px',
    textAlign: 'center',
    fontWeight: '500',
  },
  photoLikeBtn: {
    position: 'absolute',
    bottom: '52px',
    right: '12px',
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    background: 'rgba(0, 0, 0, 0.5)',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    zIndex: 3,
    transition: 'all 200ms',
    backdropFilter: 'blur(4px)',
  },
  unlockBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 14px',
    borderRadius: '20px',
    border: 'none',
    background: 'var(--accent)',
    color: '#fff',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    marginLeft: '8px',
    transition: 'all 200ms',
  },
  paymentOverlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 300,
    backdropFilter: 'blur(4px)',
  },
  paymentModal: {
    background: 'var(--bg-surface)',
    borderRadius: 'var(--radius-lg)',
    padding: '32px 24px',
    maxWidth: '340px',
    width: '100%',
    textAlign: 'center',
    border: '1px solid var(--border-color)',
    boxShadow: 'var(--shadow-lg)',
  },
};

export default function WallProfileModal({ userId, onClose }) {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [photoIndex, setPhotoIndex] = useState(0);
  const [actionLoading, setActionLoading] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [unlockLoading, setUnlockLoading] = useState(false);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await api.get(`/discover/wall/${userId}`);
        setProfile(res.data);
      } catch (err) {
        console.error('Failed to load profile:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [userId]);

  const handlePhotoTap = (e) => {
    if (!profile?.photos?.length) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    if (x > rect.width / 2) {
      setPhotoIndex((i) => Math.min(i + 1, profile.photos.length - 1));
    } else {
      setPhotoIndex((i) => Math.max(i - 1, 0));
    }
  };

  const handleLike = async () => {
    setActionLoading(true);
    try {
      const res = await api.post('/discover/swipe', { swiped_id: profile.id, action: 'like' });
      if (res.data.match) {
        setProfile((p) => ({ ...p, match_id: res.data.match_id, viewer_action: 'like' }));
      } else {
        setProfile((p) => ({ ...p, viewer_action: 'like' }));
      }
    } catch (err) {
      console.error('Like failed:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const handlePass = async () => {
    setActionLoading(true);
    try {
      await api.post('/discover/swipe', { swiped_id: profile.id, action: 'pass' });
      setProfile((p) => ({ ...p, viewer_action: 'pass' }));
    } catch (err) {
      console.error('Pass failed:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleChat = () => {
    if (profile.match_id) {
      navigate(`/chat/${profile.match_id}`);
      onClose();
    }
  };

  const handlePhotoLike = async (photoId) => {
    setActionLoading(true);
    try {
      const res = await api.post('/discover/swipe', {
        swiped_id: profile.id,
        action: 'like',
        liked_photo_id: photoId,
      });
      if (res.data.match) {
        setProfile((p) => ({ ...p, match_id: res.data.match_id, viewer_action: 'like' }));
      } else {
        setProfile((p) => ({ ...p, viewer_action: 'like' }));
      }
    } catch (err) {
      // 409 = already swiped — silently ignore
      if (err.response?.status !== 409) {
        console.error('Photo like failed:', err);
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnlock = async () => {
    setUnlockLoading(true);
    try {
      await api.post('/discover/unlock', { swiped_id: profile.id });
      setProfile((p) => ({ ...p, viewer_action: null }));
      setShowPaymentModal(false);
    } catch (err) {
      console.error('Unlock failed:', err);
    } finally {
      setUnlockLoading(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  if (loading) {
    return (
      <div style={styles.overlay} onClick={handleOverlayClick}>
        <div style={styles.modal}>
          <div className="loading-spinner" style={{ height: '300px' }}>
            <div className="spinner" />
          </div>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div style={styles.overlay} onClick={handleOverlayClick}>
        <div style={styles.modal}>
          <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted)' }}>
            Profile not found
          </div>
        </div>
      </div>
    );
  }

  const currentPhoto = profile.photos?.[photoIndex];

  return (
    <div style={styles.overlay} onClick={handleOverlayClick}>
      <div style={styles.modal}>
        {/* Photo section */}
        <div style={styles.photoSection} onClick={handlePhotoTap}>
          {profile.photos?.length > 1 && (
            <div style={styles.photoNav}>
              {profile.photos.map((_, i) => (
                <div
                  key={i}
                  style={{
                    ...styles.photoDot,
                    ...(i === photoIndex ? styles.photoDotActive : {}),
                  }}
                />
              ))}
            </div>
          )}

          <button style={styles.closeBtn} onClick={(e) => { e.stopPropagation(); onClose(); }}>
            ×
          </button>

          {/* Photo like button — only show when not yet swiped and not matched */}
          {currentPhoto && !profile.viewer_action && !profile.match_id && (
            <button
              style={styles.photoLikeBtn}
              onClick={(e) => {
                e.stopPropagation();
                handlePhotoLike(currentPhoto.id);
              }}
              disabled={actionLoading}
              title="Like this photo"
            >
              <HeartFillIcon size={20} color="#E8576D" />
            </button>
          )}

          {currentPhoto ? (
            <img
              src={`/uploads/${currentPhoto.filename}`}
              alt={profile.display_name}
              style={styles.photo}
            />
          ) : (
            <div style={styles.noPhoto}>?</div>
          )}

          <div style={styles.gradient} />

          <div style={styles.nameOverlay}>
            <div style={styles.name}>
              {profile.display_name}{profile.age ? `, ${profile.age}` : ''}
            </div>
            <div style={styles.metaRow}>
              {profile.gender && <span className="badge badge-accent">{profile.gender}</span>}
              {profile.pronouns && <span className="badge badge-muted">{profile.pronouns}</span>}
              {profile.distance != null && (
                <span className="badge badge-muted">{profile.distance} mi away</span>
              )}
            </div>
          </div>
        </div>

        {/* Status banner */}
        {profile.viewer_action === 'like' && !profile.match_id && (
          <div style={{ ...styles.statusBanner, background: 'var(--accent-soft)', color: 'var(--accent)' }}>
            You liked this person
          </div>
        )}
        {profile.viewer_action === 'pass' && (
          <div style={{ ...styles.statusBanner, background: 'var(--bg-elevated)', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
            You passed on this person
            <button
              style={styles.unlockBtn}
              onClick={() => setShowPaymentModal(true)}
            >
              <LockIcon size={14} /> Unlock $0.99
            </button>
          </div>
        )}
        {profile.match_id && (
          <div style={{ ...styles.statusBanner, background: 'rgba(34, 197, 94, 0.08)', color: 'var(--success)' }}>
            You matched! Start a conversation below.
          </div>
        )}

        {/* Scrollable content */}
        <div style={styles.content}>
          {profile.bio && <p style={styles.bio}>{profile.bio}</p>}

          {/* Prompts */}
          {profile.prompts?.length > 0 && (
            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>Prompts</h3>
              {profile.prompts.map((p) => (
                <div key={p.prompt_key} style={styles.promptCard}>
                  <p style={styles.promptQ}>{PROMPT_TEXTS[p.prompt_key] || p.prompt_key}</p>
                  <p style={styles.promptA}>{p.response}</p>
                </div>
              ))}
            </div>
          )}

          {/* Traits */}
          {profile.traits?.length > 0 && (
            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>About them</h3>
              <div style={styles.traitChips}>
                {profile.traits.map((t) => {
                  let display = t.trait_value;
                  try { display = JSON.parse(t.trait_value); } catch {}
                  if (Array.isArray(display)) display = display.join(', ');
                  return (
                    <span key={t.trait_key} style={styles.traitChip}>
                      {TRAIT_LABELS[t.trait_key] || t.trait_key}: {display}
                    </span>
                  );
                })}
              </div>
            </div>
          )}

          {/* Preferences */}
          {profile.preferences?.length > 0 && (
            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>Looking for</h3>
              <div style={styles.traitChips}>
                {profile.preferences
                  .filter((p) => p.pref_value !== 'No preference')
                  .map((p) => {
                    let display = p.pref_value;
                    try { display = JSON.parse(p.pref_value); } catch {}
                    if (Array.isArray(display)) display = display.join(', ');
                    return (
                      <span key={p.pref_key} style={styles.traitChip}>
                        {TRAIT_LABELS[p.pref_key] || p.pref_key}: {display}
                      </span>
                    );
                  })}
              </div>
            </div>
          )}

          {/* Vaccinations */}
          {profile.vaccinations?.length > 0 && (
            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>Vaccination Status</h3>
              <div style={styles.vaxGrid}>
                {profile.vaccinations.map((v) => (
                  <span
                    key={v.vaccine_type}
                    className={`badge ${v.status === 'vaccinated' ? 'badge-success' : v.status === 'partial' ? 'badge-warning' : 'badge-muted'}`}
                  >
                    {v.vaccine_type.replace(/_/g, ' ')}
                    {v.status === 'vaccinated' ? ' ✓' : v.status === 'partial' ? ' ~' : ''}
                  </span>
                ))}
              </div>
            </div>
          )}
          {profile.vaccination_opted_out && (
            <div style={styles.section}>
              <span className="badge badge-muted">Vaccination status: Opted out</span>
            </div>
          )}
        </div>

        {/* Action buttons */}
        <div style={styles.actions}>
          {profile.match_id ? (
            <button
              style={{ ...styles.actionBtn, ...styles.chatBtn }}
              onClick={handleChat}
            >
              💬 Start Chat
            </button>
          ) : profile.viewer_action === 'like' ? (
            <div style={{ flex: 1, textAlign: 'center', color: 'var(--text-muted)', fontSize: '13px', padding: '10px' }}>
              Liked — waiting for them to swipe back
            </div>
          ) : profile.viewer_action === 'pass' ? (
            <button
              style={{ ...styles.actionBtn, ...styles.likeBtn }}
              onClick={() => setShowPaymentModal(true)}
            >
              <LockIcon size={16} /> Unlock $0.99
            </button>
          ) : (
            <>
              <button
                style={{ ...styles.actionBtn, ...styles.passBtn }}
                onClick={handlePass}
                disabled={actionLoading}
              >
                ✕ Pass
              </button>
              <button
                style={{ ...styles.actionBtn, ...styles.likeBtn }}
                onClick={handleLike}
                disabled={actionLoading}
              >
                ♥ Like
              </button>
            </>
          )}
        </div>

        {/* Fake payment modal */}
        {showPaymentModal && (
          <div
            style={styles.paymentOverlay}
            onClick={(e) => { if (e.target === e.currentTarget) setShowPaymentModal(false); }}
          >
            <div style={styles.paymentModal}>
              <div style={{ fontSize: '48px', marginBottom: '12px' }}>
                <LockIcon size={48} color="var(--accent)" />
              </div>
              <h3 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '8px' }}>
                Unlock {profile.display_name}
              </h3>
              <p style={{ fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '24px', lineHeight: 1.5 }}>
                Changed your mind? Unlock this profile for $0.99 and get another chance to like or message them.
              </p>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  style={{
                    flex: 1,
                    padding: '12px',
                    borderRadius: 'var(--radius-sm)',
                    border: '1px solid var(--border-color)',
                    background: 'var(--bg-elevated)',
                    color: 'var(--text-secondary)',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                  }}
                  onClick={() => setShowPaymentModal(false)}
                >
                  Cancel
                </button>
                <button
                  style={{
                    flex: 1,
                    padding: '12px',
                    borderRadius: 'var(--radius-sm)',
                    border: 'none',
                    background: 'var(--accent)',
                    color: '#fff',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    opacity: unlockLoading ? 0.6 : 1,
                  }}
                  onClick={handleUnlock}
                  disabled={unlockLoading}
                >
                  {unlockLoading ? 'Processing...' : 'Pay $0.99'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
